import { API_BASE_URL, ApiError } from './client';

/**
 * A category the server offers, and whether it reaches this person — TROOP-192.
 *
 * `canMute` and `whyNot` travel from the server rather than being decided here. The screen must not
 * hold its own copy of which categories are non-declinable: §20's answer is the server's, and the
 * acceptance criterion for this ticket is that mutating a client-side check changes nothing.
 */
export interface NotificationCategoryRow {
  category:
    | 'Announcements'
    | 'EventChanges'
    | 'Messages'
    | 'FamilyObligations'
    | 'Travel'
    | 'SafetyAndAccount';
  delivers: boolean;
  canMute: boolean;
  whyNot: string | null;
}

export interface NotificationChildRow {
  personId: string;
  displayName: string;
  delivers: boolean;
}

export interface NotificationSettings {
  categories: NotificationCategoryRow[];
  children: NotificationChildRow[];

  /**
   * The weekly digest — TROOP-189. Beside the six categories rather than one of them, because it is
   * a channel and not a subject: it carries announcements, obligations, messages and travel in one
   * message, so switching it off is a different decision from muting any of them.
   *
   * True for somebody who has never touched it: absence means §20's default, the same rule the
   * categories follow.
   */
  weeklyDigest: boolean;
}

async function call<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await fetch(`${API_BASE_URL}${path}`, {
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    ...init,
  });

  if (!response.ok) {
    throw new ApiError(response.status, `${init?.method ?? 'GET'} ${path} failed`);
  }

  return response.status === 204 ? (undefined as T) : ((await response.json()) as T);
}

export const notificationsApi = {
  settings: () => call<NotificationSettings>('/me/notifications/'),

  /** `delivers` is the state being asked for, never "flip it" — so a replayed request is a no-op. */
  setCategory: (category: string, delivers: boolean) =>
    call<void>(`/me/notifications/categories/${category}`, {
      method: 'PUT',
      body: JSON.stringify({ delivers }),
    }),

  setChild: (childPersonId: string, delivers: boolean) =>
    call<void>(`/me/notifications/children/${childPersonId}`, {
      method: 'PUT',
      body: JSON.stringify({ delivers }),
    }),

  /**
   * Turning the weekly digest back on lives here and nowhere else. The unsubscribe link in the mail
   * is anonymous and forwardable, so it deliberately cannot restore somebody's mail — only stop it.
   */
  setWeeklyDigest: (delivers: boolean) =>
    call<void>('/me/notifications/digest', {
      method: 'PUT',
      body: JSON.stringify({ delivers }),
    }),
};
